import React from 'react';
import { Row, Column, Margin, Link } from '@upgrade/react-components';
import useWindowMinWidth from '@upgrade/react-components/hooks/useWindowMinWidth';
import { mdiCheck, mdiCircleMedium } from '@mdi/js';

import { reverseFocusFromColor } from '../../utils/focus-color';
import Highlights from '../common/HighlightsV2';
import Fluid from '../Fluid';

import ios from '../../../static/img/download-ios.svg';
import android from '../../../static/img/download-android.svg';

import {
  AppImage,
  InnerContainer,
  AppImageContainer,
  StyledAppLink,
  StyledDownloadAppsImg,
  StyledQrCodeImage
} from './styles';
import { Heading, Text } from '../common/TypographyPublicSite';
import CTAButton from '../common/CTAButton';
import CardSection from '../common/CardSection';

const ICON_MAP = {
  default: mdiCheck,
  circle: mdiCircleMedium
};

const Apps = ({
  downloadApps: {
    title,
    backgroundColor,
    innerColor,
    innerPadding,
    justify,
    subTitle,
    linkTitle,
    linkAddress,
    titleColor,
    subTitleColor,
    image,
    imageWidth = '350',
    imageHeight = '350',
    flipLayout,
    highlights,
    qrCodeImg,
    qrCodeImgAlt,
    qrCodeText,
    qrCodeImgHeight = '150',
    qrCodeImgWidth = '150',
    ctaLinkUrl,
    ctaButtonAriaLabel,
    ctaButtonText = 'Get Started',
    showDownloadApps = true
  },
  onCtaClicked
}) => {
  const [isSm, isMd] = useWindowMinWidth('sm', 'md');

  if (!showDownloadApps) return null;

  const align = isSm ? 'left' : 'center';
  const showQrCode = qrCodeImg && isMd;

  return (
    <CardSection backgroundColor={backgroundColor}>
      <Fluid padding="md">
        <InnerContainer data-color={innerColor} innerPadding={innerPadding}>
          <Row xsVerticalAlign="middle" reverse={flipLayout && isSm}>
            {showQrCode ? (
              <Column sm={2}>
                <Margin bottom="md">
                  <StyledQrCodeImage
                    src={qrCodeImg}
                    alt={qrCodeImgAlt}
                    width={qrCodeImgWidth}
                    height={qrCodeImgHeight}
                  />
                </Margin>
                <Text color="contentReverse" as="p" marginBottom="none" size="xxs" align="center">
                  {qrCodeText}
                </Text>
              </Column>
            ) : null}
            <Column xs={12} sm={6} xsHorizontalAlign="center">
              <Margin bottom={{ xs: 'lg', sm: 'none' }}>
                <Heading as="h2" color={titleColor} size="sm" {...{ align }}>
                  {title}
                </Heading>
                <Text as="p" color={subTitleColor} size="md" {...{ align }}>
                  {subTitle}
                </Text>
                {highlights ? (
                  <Margin bottom="sm">
                    <Highlights
                      color={highlights.color}
                      icon={ICON_MAP[highlights.icon] ?? ICON_MAP.default}
                      iconColor={highlights.iconColor}
                      iconSize="1.5rem"
                      items={highlights.items}
                      textSize={highlights.size}
                      {...{ justify }}
                    />
                  </Margin>
                ) : null}
                {ctaLinkUrl ? (
                  <Margin bottom="xl">
                    <CTAButton
                      {...{
                        emphasis: 'secondary',
                        ctaButtonText,
                        linkUrl: onCtaClicked ? undefined : ctaLinkUrl,
                        ariaLabel: ctaButtonAriaLabel,
                        buttonProps: {
                          disabled: !ctaLinkUrl,
                          'data-auto': 'download-apps-cta-button',
                          onClick: onCtaClicked
                        }
                      }}
                    />
                  </Margin>
                ) : null}
                {!showQrCode ? (
                  <AppImageContainer>
                    <Link
                      reverseFocus={reverseFocusFromColor(titleColor)}
                      href="https://apps.apple.com/us/app/upgrade/id1389859093"
                    >
                      <AppImage src={ios} alt="Download the Upgrade app for iOS" width="216" height="65" />
                    </Link>

                    <Link
                      reverseFocus={reverseFocusFromColor(titleColor)}
                      href="https://play.google.com/store/apps/details?id=com.upgrademobile"
                    >
                      <AppImage src={android} alt="Get the Upgrade app for Android" width="300" height="89" />
                    </Link>
                  </AppImageContainer>
                ) : null}

                {linkTitle && linkAddress ? (
                  <StyledAppLink to={linkAddress} type="no-underline">
                    <Text bold color="actionablePrimary" size="lg">
                      {linkTitle}
                    </Text>
                  </StyledAppLink>
                ) : null}
              </Margin>
            </Column>
            <Column xs={12} sm={6} md={showQrCode ? 4 : 6} xsHorizontalAlign="center">
              <Margin bottom={{ xs: 'lg', sm: 'none' }}>
                <StyledDownloadAppsImg
                  src={image || '/img/upgrade-card-app.png'}
                  width={imageWidth}
                  height={imageHeight}
                />
              </Margin>
            </Column>
          </Row>
        </InnerContainer>
      </Fluid>
    </CardSection>
  );
};

export default Apps;
